/**
 * Ads-specific composer.
 *
 * Wraps the standard app composer (`useAppComposer`) but injects the user's
 * current stage (the ads substep — Headlines / Highlights / Keywords / etc.)
 * into every submit envelope. Without this, chat-message submits arrive at
 * the graph with no `stage` field and the agent reuses whatever stage was
 * last set by a `switch_page` / `refresh_assets` intent — producing
 * "you're on the callouts page" responses while the user is on Headlines.
 *
 * Why composer-level (not button-level): all chat submits — typed text,
 * attached files, multimodal — funnel through the composer's `formatSubmit`.
 * Wrapping that one seam catches every path. A custom submit button would
 * need to duplicate composer plumbing (uploads, validation, attachment
 * formatting).
 */
import { useMemo, useRef } from "react";
import { createComposer, defaultFormatSubmit } from "@launch10/use-langgraph-stream";
import { UploadsAPIService } from "@rails_api_base";
import { validateFile } from "~/types/attachment";
import { uploadFile } from "~/lib/uploadPipeline";
import { useJwt, useRootPath } from "~/stores/sessionStore";
import { useWebsiteId } from "~/stores/projectStore";
import { useWorkflow, selectSubstep } from "@context/WorkflowProvider";

export function useAdsComposer() {
  const jwt = useJwt();
  const rootPath = useRootPath();

  // The composer is created once and persists across renders. We read the
  // *current* stage at submit time via a ref so navigation between tabs is
  // reflected without rebuilding the composer (which would tear down attach
  // state mid-flight).
  const stage = useWorkflow(selectSubstep);
  const stageRef = useRef(stage);
  stageRef.current = stage;

  // Same ref pattern for websiteId so uploads carry the current website
  // without rebuilding the composer when the project store updates.
  const websiteId = useWebsiteId();
  const websiteIdRef = useRef(websiteId);
  websiteIdRef.current = websiteId;

  return useMemo(() => {
    const uploadService = new UploadsAPIService({
      jwt: jwt ?? "",
      baseUrl: rootPath ?? "",
    });

    return createComposer({
      attachments: {
        upload: async (file: File) => {
          const response = await uploadFile(uploadService, file, {
            isLogo: false,
            websiteId: websiteIdRef.current ?? undefined,
          });
          // `upload_id` rides additional_kwargs.metadata.attachments so the
          // agent can link an image to its uploads row (parity with the
          // website/app composers; recommendation image fields depend on it).
          return {
            url: response.url,
            meta: { filename: response.filename, upload_id: response.id },
          };
        },
        validate: validateFile,
      },
      formatSubmit: (content) => ({
        ...defaultFormatSubmit(content),
        // Attach the live stage so handleIntentNode reads it from the
        // envelope instead of falling back to a stale checkpoint value.
        stage: stageRef.current,
      }),
    });
  }, [jwt, rootPath]);
}
